import { createAsyncThunk } from "@reduxjs/toolkit"

import { handleApiError } from "../../../Utils/thunkHelpers"
import utils from "../../../Utils/utils"
import * as services from "../services"
import { BUSINESS_ACCOUNTS_SLICE_NAME } from "./businessAccounts.constants"

export const getStatisticsAdmin = createAsyncThunk(
  `${BUSINESS_ACCOUNTS_SLICE_NAME}/get_statistics_admin`,
  async (params = {}, { dispatch, getState, rejectWithValue }) => {
    // Legacy apiGeneratorBase special-cased this exact action when it was dispatched as an
    // `extraAction` by other sagas (deleteUserBusinessAccount below, plus several sibling
    // modules), injecting `test_data` from state instead of the empty params those callers
    // passed. Reading it here means every caller gets it automatically, direct dispatch or not.
    const test_data = getState().globalFilters.testData
    const response = await services.getStatisticsAdmin({ ...params, test_data })
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getBusinessAccountsFilterOptions = createAsyncThunk(
  `${BUSINESS_ACCOUNTS_SLICE_NAME}/get_business_accounts_filter_options`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getBusinessAccountsFilterOptions()
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getAdminBusinessAccounts = createAsyncThunk(
  `${BUSINESS_ACCOUNTS_SLICE_NAME}/get_admin_business_accounts`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getAdminBusinessAccounts(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const deleteUserBusinessAccount = createAsyncThunk(
  `${BUSINESS_ACCOUNTS_SLICE_NAME}/delete_user_business_account`,
  async (businessId, { dispatch, rejectWithValue }) => {
    const response = await services.deleteUserBusinessAccount(businessId)
    if (!(response.ok && (response.data || response.status === 204))) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    dispatch(getStatisticsAdmin())
    return {
      id: businessId,
      message: response.data?.message?.detail || response.data?.message
    }
  }
)

export const getBusinessAccountsExport = createAsyncThunk(
  `${BUSINESS_ACCOUNTS_SLICE_NAME}/get_business_accounts_export`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getBusinessAccountsExport(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getAlertsChart = createAsyncThunk(
  `${BUSINESS_ACCOUNTS_SLICE_NAME}/get_alerts_chart`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getAlertsChart(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getBaCommentData = createAsyncThunk(
  `${BUSINESS_ACCOUNTS_SLICE_NAME}/get_ba_comment_data`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getBaCommentData(params)
    if (response.ok && response.data) {
      if (response.data?.redirect_url) utils.goLink(response.data.redirect_url)
      return response.data
    }
    if (response.ok && (response.status === 201 || response.status === 204)) {
      return params
    }
    await handleApiError(dispatch, response)
    return rejectWithValue(response.data)
  }
)

// BusinessAccountsPage/index.js's mapDispatchToProps strips `onSuccess` off the payload
// before dispatching and runs it off `.unwrap()` instead - the old saga stripped and called
// it the same way (apiGeneratorBaseWithOnSuccess).
export const saveBaComment = createAsyncThunk(
  `${BUSINESS_ACCOUNTS_SLICE_NAME}/save_ba_comment`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.saveBaComment(params)
    if (response.ok && response.data) {
      if (response.data?.redirect_url) utils.goLink(response.data.redirect_url)
      return response.data
    }
    if (response.ok && (response.status === 201 || response.status === 204)) {
      return params
    }
    await handleApiError(dispatch, response)
    return rejectWithValue(response.data)
  }
)

// Same `onSuccess` handling as `saveBaComment` above - stripped and run off `.unwrap()` by
// the caller (BusinessAccountsPage/index.js's mapDispatchToProps), not by this thunk.
export const deleteBaComment = createAsyncThunk(
  `${BUSINESS_ACCOUNTS_SLICE_NAME}/delete_ba_comment`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.deleteBaComment(params)
    if (!(response.ok && (response.data || response.status === 204))) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return { id: params, message: response.data?.message?.detail || response.data?.message }
  }
)

// Same `onSuccess` handling as `saveBaComment` above - stripped and run off `.unwrap()` by
// the caller (BusinessAccountsPage/index.js's mapDispatchToProps), not by this thunk.
export const deleteBaCommentAttachment = createAsyncThunk(
  `${BUSINESS_ACCOUNTS_SLICE_NAME}/delete_ba_comment_attachment`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.deleteBaCommentAttachment(params)
    if (!(response.ok && (response.data || response.status === 204))) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return { id: params, message: response.data?.message?.detail || response.data?.message }
  }
)
